import { logger } from "@devforce/trailhead-client-logger";

const DEFAULT_PLACEMENT = "top";
const NUBBIN_SIZE = 14;
const DEFAULT_STATE = {
  isOpen: false,
  isDisabled: false,
  isReady: false,
  hideQueued: false,
};
class Popover {
  constructor(trigger, popover, options = {}) {
    this.trigger = trigger;
    this.popover = popover;
    this.options = options;
    this.setup();
  }

  trigger = null;
  popover = null;
  options = {};
  popper = null;
  _state = DEFAULT_STATE;

  get state() {
    return this._state;
  }

  set state(nextState) {
    this._state = { ...this._state, ...nextState };

    if (this.options.onChange) {
      this.options.onChange(this.state);
    }
  }

  setup() {
    // Ensure previously bound listeners are removed
    this.teardown(); // Show

    this.trigger?.addEventListener("mouseenter", this.handleShowEvent);
    this.trigger?.addEventListener("focusin", this.handleShowEvent); // Hide

    this.trigger?.addEventListener("mouseleave", this.handleHideEvent);
    this.trigger?.addEventListener("focusout", this.handleHideEvent);
    this.trigger?.addEventListener("keydown", this.handleKeyDown); // Popovers

    this.trigger?.addEventListener(
      "requestopen",
      this.handleRequestOpenPopover
    );
    this.trigger?.addEventListener(
      "requestclose",
      this.handleRequestClosePopover
    );
  }

  teardown() {
    this.trigger?.removeEventListener("mouseenter", this.handleShowEvent);
    this.trigger?.removeEventListener("focusin", this.handleShowEvent);
    this.trigger?.removeEventListener("mouseleave", this.handleHideEvent);
    this.trigger?.removeEventListener("focusout", this.handleHideEvent);
    this.trigger?.removeEventListener("keydown", this.handleKeyDown);
    this.trigger?.removeEventListener(
      "requestopen",
      this.handleRequestOpenPopover
    );
    this.trigger?.removeEventListener(
      "requestclose",
      this.handleRequestClosePopover
    );
  }

  async create() {
    if (!this.trigger || !this.popover) return;
    const modifiers = [
      {
        name: "flip",
        options: {
          padding: NUBBIN_SIZE,
          fallbackPlacements: [
            "top",
            "top-start",
            "top-end",
            "bottom",
            "bottom-start",
            "bottom-end",
            "right",
            "right-start",
            "right-end",
            "left",
            "left-start",
            "left-end",
          ],
        },
      },
      {
        name: "hide",
        enabled: false,
      },
      {
        name: "preventOverflow",
        options: {
          padding: {
            top: 2,
            bottom: 2,
            left: 5,
            right: 5,
          },
        },
      },
      {
        name: "offset",
        options: {
          offset: ({ placement, reference }) => {
            if (placement.match(/^(top|bottom)/)) {
              return [0, NUBBIN_SIZE];
            }

            if (placement.match(/end?/)) {
              return [(reference.height / 2) * 1, NUBBIN_SIZE];
            }

            if (placement.match(/start?/)) {
              return [(reference.height / 2) * -1, NUBBIN_SIZE];
            }

            return [0, NUBBIN_SIZE];
          },
        },
      },
    ];
    const { createPopper } = await import("common/chunk_index");
    this.popper = createPopper(this.trigger, this.popover, {
      // Note: using "fixed" will cause incorrect positioning if any parent has a transform
      // https://blog.intive-fdv.com/betrayal-position-fixed/
      strategy: "fixed",
      placement: this.getPlacement(this.options.placement),
      modifiers,
    });
    this.state = {
      isReady: true,
    };
  }

  destroy() {
    this.popper?.destroy();
    this.popper = null;
    this.state = { ...DEFAULT_STATE, isDisabled: this.state.isDisabled };
  }

  async show() {
    if (this.state.isDisabled) return;
    await this.popper?.update();
    this.state = {
      isOpen: true,
    };
  }

  hide() {
    this.state = {
      isOpen: false,
    };
  }

  disable() {
    this.state = {
      isDisabled: true,
      isOpen: false,
    };
  }

  enable() {
    this.state = {
      isDisabled: false,
    };
  } // Map the "placement" property used by SLDS to the correct Popper "placement"

  getPlacement(placement = DEFAULT_PLACEMENT) {
    switch (placement) {
      case "top left":
        return "top-start";

      case "top right":
        return "top-end";

      case "right top":
        return "right-start";

      case "right bottom":
        return "right-end";

      case "bottom left":
        return "bottom-start";

      case "bottom right":
        return "bottom-end";

      case "left top":
        return "left-start";

      case "left bottom":
        return "left-end";

      default:
        return placement;
    }
  }

  handleShowEvent = () => {
    this.create()
      .then(() => {
        // Only fire a show event if a hide event wasn't fired while the popper was being created
        if (!this.state.hideQueued) {
          return this.show();
        }

        this.state = {
          hideQueued: false,
        };
      })
      .catch((e) => logger.warn(e.message));
  };
  handleHideEvent = () => {
    if (!this.state.isReady) {
      this.state = {
        hideQueued: true,
      };
    } else {
      // Destroy the instance so consumers aren't required to do cleanup
      this.hide();
      this.destroy();
    }
  };
  handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      this.handleHideEvent();
    }
  };
  handleRequestOpenPopover = () => {
    this.disable();
  };
  handleRequestClosePopover = () => {
    this.enable();
  };
}

export { Popover };
